import { EMPTY_URL } from "csi/constants";
import csi from "csi/main";
import { IViewSettingOptions, ShareViewWith, ShareViewWithSubType } from "csi/middleware/customView/options/CustomViewOptions";
import { getCustomViewSharedWith, getPlayableRoles, processViewSettings } from "csi/middleware/customView/settings/settingsService";
import csiPrefs from "csi/prefs";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { submitWriter } from "csi/service/writerService";
import sessionConstants from "csi/sessionConstants";
import { sessionStore } from "csi/state/rootStore";
import { C8Node, cnl } from "csi/typings";
import { setDifference } from "csi/utility/arrayUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import Writer from "csi/xml/Writer";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ObservableSet, action, computed, makeObservable, observable, runInAction } from "mobx";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { writeRemovedCustomViewDefaults } from "../customViewSaveService";
import { ICustomViewUtil } from "../customViewService";
import { isEqual } from "lodash";

interface ViewSettingOptions extends Pick<
	CustomViewSettingsStore,
	"everyoneSetViewAsDefault" |
	"everyoneShareWithDefault" |
	"everyoneShareWithSubtypedDefault" |
	"prefIsShared" |
	"prefParty" |
	"selectedEveryoneSetViewDefaultSpecificRoles" |
	"selectedEveryoneSpecificSubType" |
	"selectedSpecificRolesSetViewDefaultSpecificRoles" |
	"selectedSpecificSubType" |
	"shareWith" |
	"shareWithSubtypedDefault" |
	"specificRolesSetViewAsDefault"> { }

/**
 * Custom view settings mobx store.
 */
export default class CustomViewSettingsStore extends ReactionStore implements ICustomViewUtil<IViewSettingOptions> {
	/** the description */
	@observable activeDescription: string;

	/** the name */
	@observable activeName: string;

	/** custom view dialog store */
	readonly customViewDialogStore: CustomViewDialogStore;

	/** the Everyone's "Set view as default" section current status (collapse/expand) */
	@observable everyoneSetViewAsDefault = false;

	/** the selected share view within "Set view as default" radio group option */
	@observable everyoneShareWithDefault: ShareViewWith;

	/** Everyone section, the selected share view within "Set view as default" subtype section radio group option */
	@observable everyoneShareWithSubtypedDefault: ShareViewWithSubType;

	/** indicates if hide the permission setting options, if if it's true, the selected custom view automatically becomes the site default custom view  */
	readonly forceShareWithEveryone: boolean = false;

	/** indicates if is editing active description */
	@observable isEditingActiveDescription: boolean = false;

	/** indicates if is editing active name */
	@observable isEditingActiveName: boolean = false;

	/** description for custom view */
	@observable prefDescription: string;

	/** indicates whether the custom view is shared with site or roles */
	@observable prefIsShared: boolean;

	/** name for custom view */
	@observable prefName: string;

	/** parties (user or role(s)) associated with custom view */
	@observable.ref prefParty: cnl[];

	/** the playable roles of the current user */
	readonly playableRoles: ComponentItem[] = getPlayableRoles();

	/** the selected roles which this custom view is set as default (Everyone) */
	@observable.ref selectedEveryoneSetViewDefaultSpecificRoles: ComponentItem[] = [];

	/** the selected everyone specific roles subtype */
	@observable.ref selectedEveryoneSpecificSubType: ComponentItem[] = [];

	/** the selected roles which use the custom view as default (Specific Roles) */
	@observable.ref selectedSpecificRolesSetViewDefaultSpecificRoles: ComponentItem[] = [];

	/** the selected specific roles subtype */
	@observable.ref selectedSpecificSubType: ComponentItem[] = [];

	/** the selected share view within radio group option */
	@observable shareWith: ShareViewWith;

	/** Specific roles section, the selected share view within "Set view as default" subtype section radio group option */
	@observable shareWithSubtypedDefault: ShareViewWithSubType;

	/** the specific roles to shared with */
	@observable.ref specificRoles: ComponentItem[] = [];

	/** the Specific roles' "Set view as default" section current status (collapse/expand) */
	@observable specificRolesSetViewAsDefault = false;

	/** the selected specific roles to shared with */
	private _selectedSet = observable.set<cnl>();

	/** the selected roles which use the custom view as default (Specific Roles) */
	@observable.ref private _selectedSetViewDefaultSpecificRoles: ComponentItem[] = [];

	@observable.ref initialViewSettingOptons: ViewSettingOptions;

	/** trimmed version of name */
	get activeNameTrimmed(): string {
		return this.activeName.trim();
	}

	/** the display label for all the states/subtypes */
	get all(): string {
		const { contextType, forState } = this.customViewDialogStore.customViewNodeContexts;
		const { resource, type } = this.customViewDialogStore.loc;

		return !forState ? resource("CustomViewAllSubtypedDefaultView", [type(contextType, true)]) : resource("CustomViewAllStatesDefaultView");
	}

	/** the display label for the subtyped view section */
	get everyoneTypeRadioLabel(): string {
		const { contextType, forState } = this.customViewDialogStore.customViewNodeContexts;
		const { resource, type } = this.customViewDialogStore.loc;

		return !forState ? resource("CustomViewSettingForTheseSubtyped", [type(contextType, true)]) : resource("CustomViewSettingFor");
	}

	/** the display label for the specific states/subtypes */
	get specific(): string {
		const { contextType, forState } = this.customViewDialogStore.customViewNodeContexts;
		const { resource, type } = this.customViewDialogStore.loc;

		return !forState ? resource("CustomViewSettingSpecificSubtyped", [type(contextType, true)]) : resource("State");
	}

	/** indicate whether the selected custom view is OOTB (out of the box) default custom view */
	get selectedCustomViewIsOOTB(): boolean {
		return this.customViewDialogStore.customViewStore.selectedCustomViewItem?.isOOTBItem;
	}

	/** disables set as default under 2 conditions
	 *  1. the selected Custom View is OOTB
	 *  2. The selected view is system wide and the user does not have custom view administrator access
	 */
	get disableSetAsDefault(): boolean {
		return this.selectedCustomViewIsOOTB || this.isPreferenceViewSystemWideAndNotCustomViewAdmin;
	}

	/** indicate current user has the permission to authorize everyone's access to the custom view */
	get canSelectEveryone(): boolean {
		return csiPrefs.hasAdminAccess() && !this.hasUserNotPlayableSharedRoles && !this.disablePermissionEditing && !this.selectedCustomViewIsOOTB;
	}

	/** indicate current user has the permission to authorize only himself/herself access to the custom view */
	get canSelectMe(): boolean {
		const { disablePermissionEditing, hasUserNotPlayableSharedRoles, isPreferenceViewSharedWithRolesAndSetAsDefaultAndNotCustomViewAdmin, isPreferenceViewSystemWideAndNotCustomViewAdmin, selectedCustomViewIsOOTB } = this;
		return !hasUserNotPlayableSharedRoles && !disablePermissionEditing && !selectedCustomViewIsOOTB && !isPreferenceViewSystemWideAndNotCustomViewAdmin && !isPreferenceViewSharedWithRolesAndSetAsDefaultAndNotCustomViewAdmin;
	}

	/** indicate current user has the permission to authorize specific role access to the custom view */
	get canSelectRoles(): boolean {
		let canSelect1 = false;
		const { selectedPreferenceView } = this.customViewDialogStore.customViewStore;

		if (!!selectedPreferenceView.urlNode) {
			// edit case, if the original view is shared view, we need editor access to do that
			// if the original view is personal, then we must have admin permission to make it system-wide or role-based
			canSelect1 = (selectedPreferenceView.isShared() && csiPrefs.hasEditAccess()) || (!selectedPreferenceView.isShared() && csiPrefs.hasAdminAccess());
		} else {
			// duplicate case, must have admin permission
			canSelect1 = csiPrefs.hasAdminAccess();
		}

		return canSelect1 && !this.disablePermissionEditing && !this.selectedCustomViewIsOOTB && !this.isPreferenceViewSystemWideAndNotCustomViewAdmin;
	}

	/** indicate if disable the ability to edit permissions */
	get disablePermissionEditing(): boolean {
		return this.customViewDialogStore.customViewStore.isViewingTheViewsOfOtherPerson;
	}

	/** indicate whether the Specific roles "set view as default" checkbox is enabled */
	get enableSpecificRolesSetViewAsDefault(): boolean {
		return (this.shareWith === ShareViewWith.ROLES) &&
			(this._selectedSet.size > 0) &&
			this.hasPermissionToSetViewAsDefault &&
			!this.selectedCustomViewIsOOTB &&
			!this.selectedSpecificRolesSetViewDefaultSpecificRoles.some((role) => role.disabled);
	};

	/** indicate whether user has the permission to update "set view as default" checkbox */
	get hasPermissionToSetViewAsDefault(): boolean {
		return csiPrefs.hasAdminAccess();
	}

	@computed get hasUserNotPlayableSharedRoles(): boolean {
		const playableSet = new Set(this.playableRoles.map((role) => role.id));
		const notPlayableSharedRoles = setDifference(this._selectedSet, playableSet);

		return notPlayableSharedRoles.length > 0;
	};

	@computed get optionValues(): IViewSettingOptions {
		const { prefDescription, prefIsShared, prefName, prefParty } = this;

		return {
			prefDescription,
			prefIsShared,
			prefName,
			prefParty,
		};
	}

	/** the selected specific roles under Specific roles, Set view as default, For these users section */
	get selectedSetViewDefaultSpecificRoles(): ComponentItem[] {
		return this._selectedSetViewDefaultSpecificRoles;
	}

	@computed get selectedSpecificRoles(): ComponentItem[] | undefined {
		const { _selectedSet, specificRoles } = this;

		return specificRoles?.filter((role) => {
			return _selectedSet.has(role.id);
		});
	};

	/** the list of sub-type values */
	get specificSubTypeOptions(): ComponentItem[] {
		return this.customViewDialogStore.customViewNodeContexts.subTypeList;
	}

	/** indicate if custom view is shared with everyone */
	get isViewSharedWithEveryone(): boolean {
		const { prefIsShared, prefParty } = this;
		return prefParty.length === 0 && prefIsShared;
	}

	/** indicate if custom view is shared with specific roles only */
	get isViewSharedWithSpecificRolesOnly(): boolean {
		const { prefIsShared, prefParty } = this;
		return prefParty.length > 0 && prefIsShared;
	}

	/** indicate if the preference view is system wide and user has no custom view administrator access */
	get isPreferenceViewSystemWideAndNotCustomViewAdmin(): boolean {
		const { selectedPreferenceView } = this.customViewDialogStore.customViewStore;
		return selectedPreferenceView.isSystemWide() && !csiPrefs.hasAdminAccess();
	}

	/** indicates if the preference view is role based and set as default and user has no custom view administrator access
	 *  Custom View Editors should not be able to change an existing view from share with roles to personal access.
	 */
	get isPreferenceViewSharedWithRolesAndSetAsDefaultAndNotCustomViewAdmin(): boolean {
		const { selectedPreferenceView } = this.customViewDialogStore.customViewStore;
		return selectedPreferenceView.isSharedWithRoles() && this.specificRolesSetViewAsDefault && !csiPrefs.hasAdminAccess();
	}

	get viewSettingOptions(): ViewSettingOptions {
		const {
			everyoneSetViewAsDefault,
			everyoneShareWithDefault,
			everyoneShareWithSubtypedDefault,
			prefIsShared,
			prefParty,
			selectedEveryoneSetViewDefaultSpecificRoles,
			selectedEveryoneSpecificSubType,
			selectedSpecificRolesSetViewDefaultSpecificRoles,
			selectedSpecificSubType,
			shareWith,
			shareWithSubtypedDefault,
			specificRolesSetViewAsDefault,
		} = this;

		return {
			everyoneSetViewAsDefault,
			everyoneShareWithDefault,
			everyoneShareWithSubtypedDefault,
			prefIsShared,
			prefParty,
			selectedEveryoneSetViewDefaultSpecificRoles,
			selectedEveryoneSpecificSubType,
			selectedSpecificRolesSetViewDefaultSpecificRoles,
			selectedSpecificSubType,
			shareWith,
			shareWithSubtypedDefault,
			specificRolesSetViewAsDefault,
		};
	}

	/** indicates whether the user (custom view editor) could unshare the view. If the view is set as default for role, editor can not unshare the view*/
	disableEditorShareViewAccess = (role: string): boolean => {
		return (this.selectedSpecificRolesSetViewDefaultSpecificRoles.find((playRole) => playRole.id === role) && !csiPrefs.hasAdminAccess());
	};

	/** set the initial value of the state variables */
	@action init = (): void => {
		const {
			isViewSharedWithEveryone,
			isViewSharedWithSpecificRolesOnly,
			playableRoles,
			prefDescription,
			prefIsShared,
			prefName,
			prefParty,
			selectedCustomViewIsOOTB,
			specificSubTypeOptions,
			customViewDialogStore: {
				customViewNodeContexts: {
					hasMultiple,
				},
				customViewContext,
				defaults,
				partyCache,
				selectedCustomViewCnl,
				selectedViewIsSystemWideDefault,
			},
		} = this;
		this.everyoneShareWithDefault = ShareViewWith.EVERYONE;

		this.resetShareViewWithEveryoneOption();
		this.resetShareViewWithRolesOption();

		processViewSettings(this, selectedCustomViewIsOOTB, selectedCustomViewCnl, customViewContext, isViewSharedWithEveryone, hasMultiple, defaults, selectedViewIsSystemWideDefault, isViewSharedWithSpecificRolesOnly, partyCache, specificSubTypeOptions, playableRoles, prefParty);

		this.everyoneShareWithSubtypedDefault ||= ShareViewWithSubType.ALL;
		this.shareWithSubtypedDefault ||= ShareViewWithSubType.ALL;
		this.shareWith = getCustomViewSharedWith(prefIsShared, prefParty);
		this.activeDescription = prefDescription;
		this.activeName = prefName;
		defaults.trackChanges();

		this.initialViewSettingOptons = this.viewSettingOptions;
	};

	@computed get isViewSettingsDirty(): boolean {
		return !isEqual(this.initialViewSettingOptons, this.viewSettingOptions);
	}

	@computed get nameErrorRes(): string {
		const { activeNameTrimmed } = this;
		const { names } = this.customViewDialogStore.customViewStore;
		const { selectedCustomViewCnl } = this.customViewDialogStore;

		if (!activeNameTrimmed) {
			return "PreferenceNameEmpty";
		}


		for (const [url, viewName] of Object.entries(names)) {
			// url could be the string "null" before it is saved
			if (String(selectedCustomViewCnl) != url && viewName === activeNameTrimmed) {
				return "PreferenceNameNotUnique";
			}
		}

		return "";
	}

	@action onBlurDescription = (): void => {
		this.prefDescription = this.activeDescription;
		this.isEditingActiveDescription = false;
	};

	@action onBlurName = (): void => {
		this.prefName = this.activeNameTrimmed;
		this.isEditingActiveName = false;
		if (this.nameErrorRes) {
			this.customViewDialogStore.switchTabToViolation();
		}
	};

	/** @override */
	@action onChangeOptions = (options: IViewSettingOptions): void => {
		const { prefDescription, prefIsShared, prefName, prefParty } = options;
		this.prefDescription = prefDescription;
		this.prefIsShared = prefIsShared;
		this.prefName = prefName;
		this.prefParty = prefParty;
		this.init();
	};

	/** update options store (specific roles) based on party cache info */
	updateSpecificRoles = (): void => {
		const {
			customViewDialogStore: {
				partyCache,
				selectedCustomViewCnl,
			},
			disableEditorShareViewAccess,
			isPreferenceViewSystemWideAndNotCustomViewAdmin,
			playableRoles,
			prefIsShared,
			prefParty,
			setSpecificRoles,
		} = this;

		let partyUrls = prefIsShared ? prefParty : [];
		// When copying from views with share with specific roles, the selected set should only contain playable roles for current user and the enabled items that were selected previously 
		if (!selectedCustomViewCnl) {
			partyUrls = partyUrls.filter((id) => playableRoles.find((playRole) => playRole.id == id));
		}
		const selectedSet = observable.set(partyUrls);
		const fullSet = new Set(partyUrls);

		playableRoles.forEach((role) => {
			fullSet.add(role.id);
		});

		const options = [...fullSet].map((id) => {
			const role = partyCache.get(id);
			const disableSelection = (!playableRoles.find((playRole) => playRole.id === id)) || isPreferenceViewSystemWideAndNotCustomViewAdmin || disableEditorShareViewAccess(id);
			return {
				disabled: disableSelection,
				id,
				name: role.$Name,
			};
		});

		setSpecificRoles(selectedSet, options);
	};

	/** set Defaults for site/role/user preferences when saving the view or when the user clicks cancel*/
	processDefaults = async (writer: Writer, cvNode?: C8Node): Promise<void> => {
		const {
			customViewDialogStore,
			enableSpecificRolesSetViewAsDefault,
			everyoneSetViewAsDefault,
			everyoneShareWithDefault,
			everyoneShareWithSubtypedDefault,
			forceShareWithEveryone,
			prefIsShared,
			shareWithSubtypedDefault,
			selectedEveryoneSetViewDefaultSpecificRoles,
			selectedEveryoneSpecificSubType,
			selectedSpecificRolesSetViewDefaultSpecificRoles,
			selectedSpecificSubType,
			specificRolesSetViewAsDefault,
		} = this;

		const {
			changedContexts,
			customViewStore: { removeOOTBCustomView, selectedPreferenceView },
			customViewContext,
			customViewNodeContexts: { hasMultiple },
			defaults,
			viewIsDirty,
		} = customViewDialogStore;

		const isAdmin = csiPrefs.hasAdminAccess();

		// set ReturnNodes to false so custom views nodes do not get added to view cache
		writer.noReturnNodes();

		if (selectedPreferenceView.canEdit()) {
			if (viewIsDirty) {
				// if previously shared view was made personal, then clear from default view maps
				if (isAdmin && selectedPreferenceView.urlNode && selectedPreferenceView.isShared() && !prefIsShared) {
					writeRemovedCustomViewDefaults(writer, selectedPreferenceView);
				}
			}
		}

		if (cvNode) {
			const idContext = customViewContext;
			const viewKey = csiPrefs.decomposeKey(idContext).view;
			// set default for specific roles
			if (specificRolesSetViewAsDefault) {
				// hasMultiple determines if product type is accounted for
				if (hasMultiple) {
					// set defaults for all product types under these specific roles
					if (shareWithSubtypedDefault === ShareViewWithSubType.ALL) {
						selectedSpecificRolesSetViewDefaultSpecificRoles.forEach((role) => {
							defaults.set(viewKey, cvNode.$URL, role.id);
						});
					} else {
						// set defaults for specific product types under these specific roles
						selectedSpecificRolesSetViewDefaultSpecificRoles.forEach((role) => {
							selectedSpecificSubType.forEach((subtype) => {
								defaults.set(subtype.id, cvNode.$URL, role.id);
							});
						});
					}
				} else {
					// if the "set view as default" is disabled, we don't allow to make changes to it, otherwise, we'll end up with permission error
					// because only admin has access to edit the role default
					if (enableSpecificRolesSetViewAsDefault) {
						// set defaults for specific roles
						selectedSpecificRolesSetViewDefaultSpecificRoles.forEach((role) => {
							defaults.set(idContext, cvNode.$URL, role.id);
						});
					}
				}
			} else if (everyoneSetViewAsDefault) {
				// set defaults for everyone
				if (hasMultiple) {
					if (everyoneShareWithDefault === ShareViewWith.ROLES) {
						// set defaults for speicifc roles and specific product types
						if (everyoneShareWithSubtypedDefault === ShareViewWithSubType.SPECIFIC) {
							selectedEveryoneSetViewDefaultSpecificRoles.forEach((role) => {
								selectedEveryoneSpecificSubType.forEach((subtype) => {
									defaults.set(subtype.id, cvNode.$URL, role.id);
								});
							});
						} else {
							// set defaults for specific roles and all product types
							selectedEveryoneSetViewDefaultSpecificRoles.forEach((role) => {
								defaults.set(viewKey, cvNode.$URL, role.id);
							});
						}
					} else {
						// share with Everyone but only set default for specific roles and product types
						if (everyoneShareWithSubtypedDefault === ShareViewWithSubType.SPECIFIC) {
							selectedEveryoneSpecificSubType.forEach((subtype) => {
								defaults.set(subtype.id, cvNode.$URL, csi.EMPTY_URL);
							});
						} else {
							// share with everyone and set default for all product types
							defaults.set(viewKey, cvNode.$URL, csi.EMPTY_URL);
						}
					}
				} else {
					// product types not included in these views. Set default for specific roles
					if (everyoneShareWithDefault === ShareViewWith.ROLES) {
						selectedEveryoneSetViewDefaultSpecificRoles.forEach((role) => {
							defaults.set(idContext, cvNode.$URL, role.id);
						});
					} else if (everyoneShareWithDefault === ShareViewWith.EVERYONE) {
						// set defaults for system wide
						defaults.set(idContext, cvNode.$URL, csi.EMPTY_URL);

						// remove OOTB Default
						removeOOTBCustomView();
					}
				}
			} else {
				// remove view from defaults if not set as default for everyone or specific roles
				defaults.remove(cvNode.$URL);
			}

			// write change to user's default view
			defaults.set(idContext, cvNode.$URL, forceShareWithEveryone ? EMPTY_URL : sessionConstants.userUrl);
			
			// if forceShareWithEveryone is true, we should remove the OOTB Custom View because the current view is now the site default view. C8-70584
			if (forceShareWithEveryone) {
				removeOOTBCustomView();
			}
		} else {
			// changes user preference when user click cancel.
			if (selectedPreferenceView.urlNode) {
				defaults.trackChanges();
				defaults.set(customViewContext, selectedPreferenceView.urlNode, forceShareWithEveryone ? csi.EMPTY_URL : sessionConstants.userUrl);
			}
		}
		changedContexts.push(...defaults.write(writer));
		await submitWriter(writer);
		csiPrefs.resetDefaults(true);

		// reset the initial view setting once we update the defualt settings
		runInAction(() => {
			this.initialViewSettingOptons = this.viewSettingOptions;
		});
	};

	/** Sets the Share view with, Everyone, radio group option to its default values */
	@action resetShareViewWithEveryoneOption = (): void => {
		this.everyoneSetViewAsDefault = false;
		this.everyoneShareWithDefault = ShareViewWith.EVERYONE;
		this.selectedEveryoneSetViewDefaultSpecificRoles = [];
		this.everyoneShareWithSubtypedDefault = ShareViewWithSubType.ALL;
		this.selectedEveryoneSpecificSubType = [];
	};

	/** Sets the Share view with, Specific roles, radio group option to its default values */
	@action resetShareViewWithRolesOption = (): void => {
		this.specificRolesSetViewAsDefault = false;
		this.selectedSpecificRolesSetViewDefaultSpecificRoles = [];
		this.shareWithSubtypedDefault = ShareViewWithSubType.ALL;
		this._selectedSet.clear();
		this.selectedSpecificSubType = [];
	};

	@action setActiveDescription = (event: React.ChangeEvent<HTMLInputElement>): void => {
		this.activeDescription = event.target.value;
	};

	@action setActiveName = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const name = event.target.value;
		this.activeName = name;
	};

	/** set is editing active description */
	@action setIsEditingActiveDescription = (isEditing: boolean): void => {
		this.isEditingActiveDescription = isEditing;
	};

	/** set is editing active name */
	@action setIsEditingActiveName = (isEditing: boolean): void => {
		this.isEditingActiveName = isEditing;
	};

	/** reset View settings' share view with options to its default state */
	@action resetShareViewWithOptions = (): void => {
		const { everyoneSetViewAsDefault, selectedEveryoneSetViewDefaultSpecificRoles, selectedSpecificRoles, selectedSpecificRolesSetViewDefaultSpecificRoles } = this;

		if (selectedEveryoneSetViewDefaultSpecificRoles.length === 0 && !everyoneSetViewAsDefault) {
			this.resetShareViewWithEveryoneOption();
		}

		if (selectedSpecificRoles.length === 0 && selectedSpecificRolesSetViewDefaultSpecificRoles.length === 0) {
			this.resetShareViewWithRolesOption();
		}
	};

	/** Sets share view with option, Everyone, For these users, radio group option */
	@action setEveryoneShareWithDefault = (event: object, value: ShareViewWith): void => {
		this.everyoneShareWithDefault = value;
	};

	/** Everyone, Sets the default view, For these subtype section */
	@action setEveryoneShareWithSubtypedDefault = (event: object, value: ShareViewWithSubType): void => {
		this.everyoneShareWithSubtypedDefault = value;
	};

	/** Sets the selected everyone specific roles subtype */
	@action setEveryoneSpecificSubType = (event: React.ChangeEvent, subtypes: ComponentItem[]): void => {
		this.selectedEveryoneSpecificSubType = subtypes;
	};

	/** Sets the user selected specific roles to use the custom view as default */
	@action setSelectedEveryoneSetViewDefaultSpecificRoles = (event: React.ChangeEvent, roles: ComponentItem[]): void => {
		this.selectedEveryoneSetViewDefaultSpecificRoles = roles;
	};

	/** Sets the selected specific roles subtype */
	@action setSelectedSpecificSubType = (event: React.ChangeEvent, subtypes: ComponentItem[]): void => {
		this.selectedSpecificSubType = subtypes;
	};

	/** Sets the user selected specific roles */
	@action setSelectedSpecificRoles = (event: React.ChangeEvent, roles: ComponentItem[]): void => {
		this._selectedSet.clear();
		if (roles.length > 0) {
			roles.forEach((role) => {
				this._selectedSet.add(role.id);
			});
			this.prefParty = Array.from(this._selectedSet);
			this.removeModifiedDefaultViews(this.customViewDialogStore.customViewStore.selectedPreferenceView, this.prefParty);
		} else {
			this.specificRolesSetViewAsDefault = false;
		}
	};

	/** Sets the share view with option */
	@action setShareWith = (event: object, value: ShareViewWith): void => {
		this.shareWith = value;

		// If the user did not select share with everyone, clear out options selected for share with everyone and reset it to its default state
		if (value !== ShareViewWith.EVERYONE) {
			this.resetShareViewWithEveryoneOption();
		}

		// If the user did not select share with specific roles, clear out options selected for share with specific roles and reset it to its default state
		if (value !== ShareViewWith.ROLES) {
			this.resetShareViewWithRolesOption();
			this.prefParty = [];
		}

		if (value === ShareViewWith.ME) {
			const { currentSelectedUser, hasAdminAccess } = this.customViewDialogStore.customViewStore;
			const party = hasAdminAccess ? sessionStore.userUrl : currentSelectedUser.id;

			this.prefParty = [party];
		}

		// switching to share with Everyone or share with ME resets the remove default settings for view
		if (value === ShareViewWith.EVERYONE || value === ShareViewWith.ME) {
			this.removeModifiedDefaultViews(this.customViewDialogStore.customViewStore.selectedPreferenceView, this.prefParty);
		}
		this.prefIsShared = value !== ShareViewWith.ME;
	};

	/** reset defaults of view if share with option has changed */
	removeModifiedDefaultViews = (customView: PreferenceView, newParty: cnl[]): void => {
		// admins are only ones that can change defaults
		if (!csiPrefs.hasAdminAccess()) {
			return;
		}

		const defaults = this.customViewDialogStore.defaults;
		const viewUrl = customView.getUrl();

		if (customView.isSystemWide()) {
			// if changed sharing from system wide to role or personal, then any defaults to view should be reset
			defaults.remove(viewUrl);
		} else {
			// otherwise, check if any roles removed from view, and if so remove any defaults affected
			customView.getRemovedRolesFromParty(newParty).forEach((roleUrl) => defaults.remove(viewUrl, roleUrl));
		}
	};


	/** Specific roles, Sets the default view, For these subtype section */
	@action setShareWithSubtypedDefault = (event: object, value: ShareViewWithSubType): void => {
		this.shareWithSubtypedDefault = value;
	};

	/** Sets the complete option list for specific roles and selected specific roles set */
	@action setSpecificRoles = (selectedSet: ObservableSet<cnl>, roles: ComponentItem[]): void => {
		this.specificRoles = roles;
		this._selectedSet.replace(selectedSet);
	};

	@action toggleEveryoneSetViewAsDefault = (): void => {
		this.everyoneSetViewAsDefault = !this.everyoneSetViewAsDefault;
	};

	@action toggleSpecificRolesSetViewAsDefault = (): void => {
		this.specificRolesSetViewAsDefault = !this.specificRolesSetViewAsDefault;
		this.selectedSpecificRolesSetViewDefaultSpecificRoles = [];
		this.selectedSpecificSubType = [];
	};

	/** Sets the selected view default specific roles */
	@action updateSelectedSetViewDefaultSpecificRoles = (event: React.ChangeEvent, roles: ComponentItem[]): void => {
		this._selectedSetViewDefaultSpecificRoles = roles;
	};

	/** Sets the selected view default specific roles */
	@action updateSelectedSpecificRolesSetViewDefaultSpecificRoles = (event: React.ChangeEvent, roles: ComponentItem[]): void => {
		this.selectedSpecificRolesSetViewDefaultSpecificRoles = roles;
	};

	constructor(customViewDialogStore: CustomViewDialogStore, viewSettingOptions: IViewSettingOptions, forceShareWithEveryone: boolean) {
		super();
		makeObservable(this);
		this.customViewDialogStore = customViewDialogStore;
		this.forceShareWithEveryone = forceShareWithEveryone;
		this.onChangeOptions(viewSettingOptions);
	}
}